import {Suspense, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {type Station, TimeSlot} from '@di-shared/api/di/__generated';
import {DEFAULT_CACHE_TIME} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import AnalyticPageView from '@di-shared/features/analytics/AnalyticPageView';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {VEHICLE_INSPECTION_GA_SCREEN} from '@di-shared/features/public-service/vehicle-inspection/reservation/vehicleInspectionGaScreenNames';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface VehicleInspectionEditReservationFormProps {
  vin: string;
  puid: string;
  reservationNumber: string;
  step: VehicleInspectionEditReservationStep;

  updateStep(step: VehicleInspectionEditReservationStep, isReplace: boolean): void;
}

export enum VehicleInspectionEditReservationStep {
  STEP_2_PLACE, // 검사소 선택 (수정은 정보확인이 없고, 검사소 선택 및 시간 선택만 다시할 수 있음)
  STEP_2_BEFORE_PLACE, // 검사소 선택 중 과거 검사소 선택 -> fixme 이거 기획상 이 페이지가 필요하지 않음 (과거 검사소가 많은 경우 X)
  STEP_3_PERIOD, // 검사일 선택
}

interface EditReservationForm {
  stationCode: string;
}

interface EditReservationFormExtra {
  stationName: string;
}

function VehicleInspectionEditReservationForm(props: VehicleInspectionEditReservationFormProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  return (
    <ErrorBoundary
      fallback={<Step2PlaceLoading />}
      onError={async (error: any) => {
        await openAlert({
          title: t('T40169'),
          message: `error [${error.resultCode || 9999}-${props.reservationNumber}]`,
        });
        WebviewUtils.close();
      }}
    >
      <Suspense fallback={<Step2PlaceLoading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, puid, reservationNumber, step, updateStep}: VehicleInspectionEditReservationFormProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {data: basicInfo} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getReservationStatus), puid, reservationNumber],
    queryFn: () => diApi.publicServiceInspection.getReservationStatus({puid, vin}),
    select(data) {
      if (!data.data || !data.data.statusList || data.data.statusList.length === 0) {
        throw new ExpectedError('Reservation not found');
      }
      const item = data.data.statusList.find((item) => item.reservationNumber === reservationNumber);
      if (!item) {
        throw new ExpectedError('Reservation not found');
      }
      return item;
    },
  });

  const [form, setForm] = useState<EditReservationForm>({
    stationCode: basicInfo.stationCode,
  });

  const [formExtra, setFormExtra] = useState<EditReservationFormExtra>({
    stationName: basicInfo.stationName,
  });

  function updateForm(partialForm: Partial<EditReservationForm>) {
    setForm({...form, ...partialForm});
  }

  function updateFormExtra(partialFormExtra: Partial<EditReservationFormExtra>) {
    setFormExtra({...formExtra, ...partialFormExtra});
  }

  const [submitReservation] = usePendingHandler(
    async (date: Date) => {
      if (!form.stationCode) {
        return;
      }

      WebviewUtils.loading(true);
      await diApi.publicServiceInspection.updateReservation({
        requestBody: {
          puid,
          reservationNumber,
          stationCode: form.stationCode,
          reservationDateTime: date.toISOString(),
        },
      });
      WebviewUtils.loading(false);

      await openAlert({
        title: t('T40228'),
        message: t('T40229'),
      });
      /**
       * 예약 변경이 완료되면 그냥 이 화면을 닫고, 자연스럽게 바닥으로 내려가도록
       */
      WebviewUtils.close();
    },
    async (error: any) => {
      WebviewUtils.loading(false);
      if (error.resultCode && error.resultCode === 110) {
        await openAlert({
          title: t('T40170'),
          message: t('T40171'),
          okText: t('T40172'),
        });
        emitEventLog({
          event: 'public_service_interaction',
          data_action: 'duplicate reservation confirmation',
          data_name: t('T40172'),
        });
        WebviewUtils.close();
        return;
      }
      await openAlert({
        title: t('T40226'),
        message: `error [${error.resultCode}]`,
      });
    }
  );

  return (
    <>
      {step === VehicleInspectionEditReservationStep.STEP_2_PLACE && (
        <ErrorBoundary
          fallback={<Step2PlaceLoading />}
          onError={async (error: any) => {
            await openAlert({
              title: t('T40174'),
              message: `error [${error.resultCode || 9999}]`,
            });
            WebviewUtils.close();
          }}
        >
          <Suspense fallback={<Step2PlaceLoading />}>
            <Step2Place
              puid={puid}
              reservationNumber={reservationNumber}
              licensePlateNumber={basicInfo.licensePlateNumber}
              stationCode={form.stationCode === '' ? null : form.stationCode}
              onNext={(stationCode, stationName) => {
                updateForm({stationCode});
                updateFormExtra({stationName});
                updateStep(VehicleInspectionEditReservationStep.STEP_3_PERIOD, false);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {step === VehicleInspectionEditReservationStep.STEP_3_PERIOD && (
        <ErrorBoundary
          fallback={<Step3PeriodLoading />}
          onError={async (error: any) => {
            await openAlert({
              title: t('T40175'),
              message: `error [${error.resultCode || 9999}]`,
            });
            WebviewUtils.close();
          }}
        >
          <Suspense fallback={<Step3PeriodLoading />}>
            <Step3Period
              puid={puid}
              reservationNumber={reservationNumber}
              stationCode={form.stationCode}
              stationName={formExtra.stationName}
              licensePlateNumber={basicInfo.licensePlateNumber}
              onPrev={() => updateStep(VehicleInspectionEditReservationStep.STEP_2_PLACE, false)}
              onNext={submitReservation}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
}

function Step2Place({
  puid,
  reservationNumber,
  licensePlateNumber,
  stationCode,
  onNext,
}: {
  puid: string;
  reservationNumber: string;
  licensePlateNumber: string;
  stationCode: string | null;
  onNext(stationCode: string, stationName: string): void;
}) {
  const {t} = useInferableTranslation();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getAllStations), puid, licensePlateNumber],
    queryFn: () => diApi.publicServiceInspection.getAllStations({puid, licensePlateNumber}),
    /**
     * 내용이 많은 조회라 가능한 캐싱
     */
    ...DEFAULT_CACHE_TIME,
  });

  const beforeVisitedStations = data.data.stationList.filter((station) => station.isVisitedBefore);

  const regionHierarchy = getRegionHierarchy(data.data.stationList);
  const level1Regions = Object.keys(regionHierarchy);
  const [selectedLevel1Region, setSelectedLevel1Region] = useState<string>(getInitLevel1Region(stationCode, level1Regions));
  const [level2Regions, setLevel2Regions] = useState<Array<string>>(regionHierarchy[selectedLevel1Region]);
  const [selectedLevel2Region, setSelectedLevel2Region] = useState<string>(getInitLevel2Region(stationCode, level2Regions));

  const [selectedStationCode, setSelectedStationCode] = useState<string | null>(stationCode);

  const stations = data.data.stationList.filter(
    (station) => station.regionLevel1 === selectedLevel1Region && station.regionLevel2 === selectedLevel2Region
  );

  const isValid = selectedStationCode !== null;

  function getRegionHierarchy(stationList: Array<Station>) {
    const result: Record<string, string[]> = {};
    stationList.forEach((station) => {
      if (!result[station.regionLevel1]) {
        result[station.regionLevel1] = [];
      }
      if (result[station.regionLevel1].includes(station.regionLevel2)) {
        return;
      }
      result[station.regionLevel1].push(station.regionLevel2);
    });

    return result;
  }

  function getInitLevel1Region(stationCode: string | null | undefined, level1Regions: Array<string>) {
    if (!stationCode) {
      return level1Regions[0];
    }
    const station = data.data.stationList.find((item) => item.stationCode === stationCode);
    if (!station) {
      return level1Regions[0];
    }
    return station.regionLevel1;
  }

  function getInitLevel2Region(stationCode: string | null | undefined, level2Regions: Array<string>) {
    if (!stationCode) {
      return level2Regions[0];
    }
    const station = data.data.stationList.find((item) => item.stationCode === stationCode);
    if (!station) {
      return level2Regions[0];
    }
    return station.regionLevel2;
  }

  return (
    <div className="wrap">
      <AnalyticPageView screenName={VEHICLE_INSPECTION_GA_SCREEN.RESERVATION_$RESERVATION_NUMBER__STEP_2_PLACE(reservationNumber)} />
      <Header right={<HeaderCloseButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T30574')}</h1>
      </Header>
      <main className="content">
        <section className="section">
          <div className="place_list_scroll">
            <h3 className="title">{t('T40206')}</h3>
            {beforeVisitedStations.length === 0 ? (
              <ul className="place_list">
                <li className="place_item type_empty">
                  <p className="desc">{t('T40207')}</p>
                </li>
              </ul>
            ) : (
              <ul className="place_list">
                {beforeVisitedStations.map((station) => (
                  <li
                    key={station.stationCode}
                    className="place_item"
                  >
                    <div className="place_info">
                      <span className="place_name">{station.stationName}</span>
                      <p className="address">{station.address}</p>
                      <span className="number">{station.phoneNumber}</span>
                    </div>
                    <button
                      type="button"
                      className={clsx('btn_choice', {is_active: station.stationCode === selectedStationCode})}
                      onClick={() => setSelectedStationCode(station.stationCode)}
                    >
                      <span className="ic ic_check_12px"></span>
                      {t('T10321')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="divider divider_thick"></div>
          </div>
          <div className="tab_box_wrap">
            <div className="tab_box">
              <h3 className="title">{t('T40208')}</h3>
              <ul className="tab_list">
                {level1Regions.map((region) => (
                  <li
                    key={region}
                    className={clsx('tab_item', {
                      is_active: region === selectedLevel1Region,
                    })}
                  >
                    <button
                      type="button"
                      className="btn btn_tab"
                      onClick={() => {
                        setSelectedLevel1Region(region);
                        setSelectedLevel2Region(regionHierarchy[region][0]);
                        setLevel2Regions(regionHierarchy[region]);
                      }}
                    >
                      {region}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="tab_box sub_box">
              <ul className="tab_list">
                {level2Regions.map((region) => (
                  <li
                    key={region}
                    className={clsx('tab_item', {
                      is_active: region === selectedLevel2Region,
                    })}
                  >
                    <button
                      type="button"
                      className="btn btn_tab"
                      onClick={() => {
                        setSelectedLevel2Region(region);
                      }}
                    >
                      {region}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <ul className="place_list">
            {stations.map((station) => (
              <li
                key={station.stationCode}
                className="place_item"
              >
                <div className="place_info">
                  <span className="place_name">{station.stationName}</span>
                  <p className="address">{station.address}</p>
                  <span className="number">{station.phoneNumber}</span>
                </div>
                <button
                  type="button"
                  className={clsx('btn_choice', {is_active: station.stationCode === selectedStationCode})}
                  onClick={() => setSelectedStationCode(station.stationCode)}
                >
                  <span className="ic ic_check_12px"></span>
                  {t('T10321')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled={!isValid}
          onClick={() => {
            const station = data.data.stationList.find((station) => station.stationCode === selectedStationCode)!;
            onNext(station.stationCode, station.stationName);
          }}
        >
          {t('T20348')}
        </button>
      </div>
    </div>
  );
}

function Step3Period({
  puid,
  reservationNumber,
  stationCode,
  stationName,
  licensePlateNumber,
  onPrev,
  onNext,
}: {
  puid: string;
  reservationNumber: string;
  stationCode: string;
  stationName: string;
  licensePlateNumber: string;
  onPrev(): void;
  onNext(date: Date): void;
}) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getAvailableDates), puid, stationCode, licensePlateNumber],
    queryFn: () => diApi.publicServiceInspection.getAvailableDates({puid, stationCode, licensePlateNumber}),
  });

  const availableDates = data.data.availableDates
    .map((dateString) => parseDateWithFormat(dateString))
    .filter(Boolean)
    .sort((a, b) => a!.getTime() - b!.getTime()) as Array<Date>;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null); // '08:20', '14:00'
  const [amTimes, setAmTimes] = useState<Array<TimeSlot>>([]);
  const [pmTimes, setPmTimes] = useState<Array<TimeSlot>>([]);

  const isValid = !!selectedDate && !!selectedTime;

  async function updateDate(date: Date | null) {
    if (!date) {
      return;
    }
    try {
      setSelectedDate(date);
      setSelectedTime(null);
      const {data} = await diApi.publicServiceInspection.getAvailableTimes({
        puid,
        stationCode,
        licensePlateNumber,
        date: DashedDatetimeFormatter.format(date, 'date'),
      });
      setAmTimes(data.timeSlots.filter((item) => Number(item.time.split(':')[0]) < 12));
      setPmTimes(data.timeSlots.filter((item) => Number(item.time.split(':')[0]) >= 12));
    } catch (error: any) {
      await openAlert({
        title: t('T40209'),
        message: `error [${error.resultCode || 9999}]`,
      });
    }
  }

  function makeFullDatetime() {
    const [hour, minute] = selectedTime!.split(':');

    const date = new Date(selectedDate!);
    date.setHours(Number(hour));
    date.setMinutes(Number(minute));
    return date;
  }

  return (
    <div className="wrap">
      <AnalyticPageView screenName={VEHICLE_INSPECTION_GA_SCREEN.RESERVATION_$RESERVATION_NUMBER__STEP_3_PERIOD(reservationNumber)} />
      <Header
        left={<HeaderBackButton />}
        right={<HeaderCloseButton />}
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T30492')}</h1>
      </Header>
      <main className="content">
        <section className="section section_inspection">
          <div className="inspection_info">
            <p className="place_text">
              <span className="ic ic_basic_location_16px"></span>
              <span className="region is_select">{stationName}</span>
            </p>
            {selectedDate && (
              <p className="inspection_text is_select">
                <span className="ic ic_calendar_16px"></span>
                <span className="date is_select">{CldrDatetimeFormatter.format(selectedDate, 'Md')}</span>
                {selectedTime && <span className="time is_select">{selectedTime}</span>}
              </p>
            )}
          </div>
          <ReactDatePicker
            inline
            locale={getDateFnsLocale()}
            disabledKeyboardNavigation
            onChange={updateDate}
            selected={selectedDate}
            minDate={availableDates[0]}
            maxDate={availableDates[availableDates.length - 1]}
            filterDate={(date) => availableDates.some((availableDate) => isSameDate(availableDate, date))}
            renderCustomHeader={({date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => (
              <div className="react-datepicker__current-month">
                <button
                  type="button"
                  aria-label="Previous Month"
                  className={clsx('react-datepicker__navigation react-datepicker__navigation--previous', {
                    'navigation--disabled': prevMonthButtonDisabled,
                  })}
                  disabled={prevMonthButtonDisabled}
                  onClick={() => decreaseMonth()}
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
                </button>
                {CldrDatetimeFormatter.format(date, 'yMMM')}
                <button
                  type="button"
                  aria-label="Next Month"
                  className={clsx('react-datepicker__navigation react-datepicker__navigation--next', {
                    'navigation--disabled': nextMonthButtonDisabled,
                  })}
                  disabled={nextMonthButtonDisabled}
                  onClick={() => increaseMonth()}
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
                </button>
              </div>
            )}
          />

          <div className="divider"></div>
          {amTimes.length > 0 && (
            <div className="time_select_area">
              <h3 className="time_info">{t('T40210')}</h3>
              <div className="select_area_radio">
                {amTimes.map((item) => (
                  <div
                    key={item.time}
                    className="select_box select_box_radio"
                    tabIndex={0}
                  >
                    <input
                      type="radio"
                      name="reservationAmTimes"
                      id={`reservationTime_${item.time}`}
                      className="input"
                      tabIndex={-1}
                      checked={selectedTime === item.time}
                      disabled={item.remaining === 0}
                      onChange={() => setSelectedTime(item.time)}
                    />
                    <label
                      htmlFor={`reservationTime_${item.time}`}
                      className="label"
                    >
                      <span className="text">{item.time}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          {pmTimes.length > 0 && (
            <div className="time_select_area">
              <h3 className="time_info">{t('T40211')}</h3>
              <div className="select_area_radio">
                {pmTimes.map((item) => (
                  <div
                    key={item.time}
                    className="select_box select_box_radio"
                    tabIndex={0}
                  >
                    <input
                      type="radio"
                      name="resercvationPmTimes"
                      id={`reservationTime_${item.time}`}
                      className="input"
                      tabIndex={-1}
                      checked={selectedTime === item.time}
                      disabled={item.remaining === 0}
                      onChange={() => setSelectedTime(item.time)}
                    />
                    <label
                      htmlFor={`reservationTime_${item.time}`}
                      className="label"
                    >
                      <span className="text">{item.time}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() => onPrev()}
          >
            {t('T10316')}
          </button>

          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!isValid}
            onClick={() => {
              emitEventLog({
                event: 'public_service_interaction',
                data_action: 'vehicle inspection click',
                data_name: 'inspection date next',
              });
              onNext(makeFullDatetime());
            }}
          >
            {t('T10322')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Step2PlaceLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <Header right={<HeaderCloseButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T30574')}</h1>
      </Header>
      <main className="content">
        <section className="section">
          <div className="place_list_scroll">
            <h3 className="title">{t('T40206')}</h3>
            <ul className="place_list">
              <li className="place_item type_empty skeleton">
                <p className="desc">&nbsp;</p>
              </li>
            </ul>
            <div className="divider divider_thick"></div>
          </div>
          <div className="tab_box_wrap">
            <div className="tab_box">
              <h3 className="title">{t('T40208')}</h3>
              <ul className="tab_list skeleton">
                <li className="tab_item">
                  <button
                    type="button"
                    className="btn btn_tab"
                  >
                    &nbsp;
                  </button>
                </li>
              </ul>
            </div>
            <div className="tab_box sub_box">
              <ul className="tab_list skeleton">
                <li className="tab_item">
                  <button
                    type="button"
                    className="btn btn_tab"
                  >
                    &nbsp;
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <ul className="place_list">
            <li
              className="place_item skeleton"
              style={{marginBottom: 8}}
            >
              <div className="place_info">
                <span className="place_name">&nbsp;</span>
                <p className="address">&nbsp;</p>
                <span className="number">&nbsp;</span>
              </div>
            </li>
            <li
              className="place_item skeleton"
              style={{marginBottom: 8}}
            >
              <div className="place_info">
                <span className="place_name">&nbsp;</span>
                <p className="address">&nbsp;</p>
                <span className="number">&nbsp;</span>
              </div>
            </li>
            <li
              className="place_item skeleton"
              style={{marginBottom: 8}}
            >
              <div className="place_info">
                <span className="place_name">&nbsp;</span>
                <p className="address">&nbsp;</p>
                <span className="number">&nbsp;</span>
              </div>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}

function Step3PeriodLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <Header
        left={<HeaderBackButton />}
        right={<HeaderCloseButton />}
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T30492')}</h1>
      </Header>
      <main className="content">
        <section className="section section_inspection">
          <div className="inspection_info skeleton">
            <p className="place_text">
              <span className="ic ic_basic_location_16px"></span>
              <span className="region is_select">&nbsp;</span>
            </p>
            <p className="inspection_text is_select">
              <span className="ic ic_calendar_16px"></span>
              <span className="date is_select">&nbsp;</span>
              <span className="time is_select">&nbsp;</span>
            </p>
          </div>
          <div className="react-datepicker skeleton">
            <div style={{height: 376}}></div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default VehicleInspectionEditReservationForm;
